import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { filter, Observable, of, shareReplay, switchMap, take, throwError } from 'rxjs';
import { IEvent, IEventCreate, IEventUpdate } from '@app/api/interfaces/event.interface';
import {
  createEvent,
  createEventFailure,
  createEventSuccess,
  loadEventHistory,
  updateEvent,
  updateEventFailure,
  updateEventSuccess,
} from '@app/main/main-screen/store/data/events/events.actions';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectMainAreaId } from '@app/main/main-screen/store/main-selection/main-project-selection/main-project-selection.selectors';
import { IDialogEventDraft } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-draft.interface';
import { toEventUpdate } from '@app/main/leak-detection/dialogs/dialog-event/utils/dialog-event-update';

@Injectable({ providedIn: 'root' })
export class DialogEventPersistenceService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private readonly projectId = this.store.selectSignal(selectMainProjectId);
  private readonly areaId = this.store.selectSignal(selectMainAreaId);

  public create(draft: IDialogEventDraft, comment?: string): Observable<IEvent> {
    const projectId = this.projectId();
    const areaId = this.areaId();

    if (!draft.coordinates) return throwError(() => new Error('an event cannot be created without coordinates'));
    if (!projectId || !areaId) return throwError(() => new Error('no project or area is selected'));

    const data: IEventCreate = {
      areaId,
      coordinates: draft.coordinates,
      location: draft.location,
      assignedTo: draft.assignedTo,
      // the note the popup was created with becomes the event's first history entry
      ...(comment ? { comment } : {}),
    };

    return this.dispatchWrite(requestId => createEvent({ requestId, projectId, data })).pipe(
      switchMap(event => this.patchDiff(event, draft))
    );
  }

  public save(current: IEvent, draft: IDialogEventDraft, comment?: string): Observable<IEvent> {
    return this.patchDiff(current, draft, comment);
  }

  public saveAssignee(eventId: string, assignedTo: string | null): Observable<IEvent> {
    return this.update(eventId, { assignedTo });
  }

  public setHidden(eventId: string, hidden: boolean): Observable<IEvent> {
    return this.update(eventId, { hidden });
  }

  public addComment(eventId: string, comment: string): Observable<IEvent> {
    return this.update(eventId, { comment });
  }

  public loadHistory(eventId: string): void {
    const projectId = this.projectId();

    if (projectId) this.store.dispatch(loadEventHistory({ projectId, eventId }));
  }

  public update(eventId: string, data: IEventUpdate): Observable<IEvent> {
    const projectId = this.projectId();

    if (!projectId) return throwError(() => new Error('no project is selected'));

    return this.dispatchWrite(requestId => updateEvent({ requestId, projectId, eventId, data }));
  }

  private dispatchWrite(build: (requestId: string) => Action & { requestId: string }): Observable<IEvent> {
    const requestId = crypto.randomUUID();

    const outcome$ = this.actions$.pipe(
      ofType(createEventSuccess, createEventFailure, updateEventSuccess, updateEventFailure),
      filter(action => action.requestId === requestId),
      take(1),
      switchMap(action => ('error' in action ? throwError(() => action.error) : of(action.event))),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    outcome$.subscribe({ error: () => undefined });
    this.store.dispatch(build(requestId));

    return outcome$;
  }

  private patchDiff(current: IEvent, draft: IDialogEventDraft, comment?: string): Observable<IEvent> {
    const data = toEventUpdate(current, draft);

    if (!Object.keys(data).length && !comment) return of(current);

    return this.update(current.id, { ...data, ...(comment ? { comment } : {}) });
  }
}
